import { useState, useEffect } from 'react';
import NemaProgramEditor from './NemaProgramEditor';
import DemandSection from './NeDemandSection';
import SignalHelp, { Section } from './NeSignalHelp';
import CostHelp from './NeCostHelp';
import CapacityHelp from './NeCapacityHelp';
import CostRow from './NeCostRow';
import SafetyStatus from './NeSafetyStatus';
import { FaTimes, FaBan, FaUndo, FaArrowLeft, FaPlay, FaSpinner, FaFileAlt, FaSignOutAlt } from 'react-icons/fa';
import { LOS_LEGEND, kmhToMph, mphToKmh, estimateCost, editKindKey, runCost }
  from '../utils/networkEditor';
import './NetworkEditorPanel.css';

const rgb = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;

function Stat({ label, value, unit, delta }) {
  return (
    <div className="ne-stat">
      <div className="ne-stat-value">
        {value}<span className="ne-stat-unit">{unit}</span>
        {delta != null && delta !== 0 && (
          <span className={`ne-stat-delta ${delta > 0 ? 'worse' : 'better'}`}>
            {delta > 0 ? '+' : ''}{delta}
          </span>
        )}
      </div>
      <div className="ne-stat-label">{label}</div>
    </div>
  );
}

// Road editor: close/reopen, plus lane and speed-limit changes.
function RoadEditor({ selected, onClose, onReopen, onChangeAttrs, onBack }) {
  const p = selected.props;
  const one = selected.oneWay || { ids: [p.id], lengthM: p.length_m || 0 };
  const both = selected.bothWays || one;
  const kmOf = (s) => ((s.lengthM || 0) / 1000).toFixed(2);
  // State is the mph integer and `dirty` compares in mph space: km/h does not
  // round-trip (48.3 → 30 mph → 48.28), which would stage an edit nobody made.
  const [lanes, setLanes] = useState(p.lanes);
  const origMph = kmhToMph(p.speed_kmh);
  const [speedMph, setSpeedMph] = useState(origMph);
  useEffect(() => { setLanes(p.lanes); setSpeedMph(kmhToMph(p.speed_kmh)); },
    [p.id, p.lanes, p.speed_kmh]);

  const dirty = lanes !== p.lanes || speedMph !== origMph;

  return (
    <div className="ne-editor">
      <button className="ne-back" onClick={onBack}><FaArrowLeft size={11} /> Back</button>
      <div className="ne-editor-title">{p.name || '(unnamed road)'}</div>
      <div className="ne-inspector-grid">
        <span>Type</span><span>{p.highway || '—'}</span>
        <span>Length</span><span>{p.length_m} m</span>
        {p.metric && <>
          <span>V/C</span><span>{p.metric.vc?.toFixed(2)} ({p.metric.los})</span>
          <span>Volume</span><span>{p.metric.count?.toLocaleString()} veh/h</span>
        </>}
      </div>

      {!p.removed && (
        <>
          <div className="ne-field">
            <label>Lanes</label>
            <input type="number" min="1" max="6" value={lanes}
              onChange={(e) => setLanes(+e.target.value)} />
          </div>
          <div className="ne-field">
            <label>Speed limit</label>
            <input type="number" min="5" max="80" step="1" value={speedMph}
              onChange={(e) => setSpeedMph(+e.target.value)} />
            <span className="ne-field-unit">mph</span>
          </div>
          {dirty && (
            <button className="ne-action stage"
              onClick={() => onChangeAttrs(p.id, { lanes, speed_kmh: mphToKmh(speedMph) })}>
              Stage these changes
            </button>
          )}
        </>
      )}

      {p.removed ? (
        <button className="ne-action reopen" onClick={() => onReopen(p, selected.bothWays?.ids)}>
          <FaUndo size={12} /> Reopen this road
        </button>
      ) : (
        <>
          <button className="ne-action close with-scope" onClick={() => onClose(p, one.ids)}>
            <span className="ne-action-label">
              <FaBan size={12} /> Close {selected.bearing || 'this direction'}
            </span>
            <span className="ne-action-scope">{one.ids.length} segments · {kmOf(one)} km</span>
          </button>
          {both.ids.length > one.ids.length && (
            <button className="ne-action close-both with-scope" onClick={() => onClose(p, both.ids)}>
              <span className="ne-action-label">
                <FaBan size={12} /> Close both directions
              </span>
              <span className="ne-action-scope">{both.ids.length} segments · {kmOf(both)} km</span>
            </button>
          )}
          <div className="ne-scope-note">
            A two-way street is two one-way links in the model, so each
            carriageway can be closed on its own.
          </div>
        </>
      )}
    </div>
  );
}

// SUMO phase state, one char per controlled movement: 'G' protected green, 'g'
// permissive green, 'y' clearance, 'r' red. Test 'y' FIRST — a clearance interval
// often keeps a movement green ('yygrryyy'), so green-first misclassifies it.
const phaseKind = (state) =>
  /y/i.test(state) ? 'yellow' : /[Gg]/.test(state) ? 'green' : 'red';

// Does this green stage yield anywhere? Protected and permissive are different
// movements with different capacity, so they are drawn apart rather than
// flattened into one shade of green.
const isPermissive = (state) => phaseKind(state) === 'green' && /g/.test(state);

// SUMO's connection `dir`: uppercase is the shallow version of the same turn.
const DIR_WORD = {
  s: 'through', t: 'U-turn', l: 'left', r: 'right',
  L: 'slight left', R: 'slight right',
};

const PHASE_COLOR = { green: '#4caf50', permissive: '#8bc34a', yellow: '#ffc107', red: '#555' };
const phaseColor = (state) =>
  (isPermissive(state) ? PHASE_COLOR.permissive : PHASE_COLOR[phaseKind(state)]);

// Read-only controller bounds out of tls.json. Written as whole seconds: they
// come through as floats (13.0) and the panel shows seconds everywhere else.
const secs = (v) => (v == null ? '—' : `${+Number(v).toFixed(1)}s`);

// Ring-barrier chart, read-only: ring1/ring2/barrierPhases/barrier2Phases are tlLogic
// params and each phase's NEMA number is its `name`. Cell widths are NOT proportional
// to time — this controller has no fixed split (actuated between minDur and maxDur).
// An empty slot is drawn empty: a two-phase junction is a standard NEMA config.
function RingBarrierChart({ nema, byNumber, describe }) {
  const r1 = nema.ring1 || [0, 0, 0, 0];
  const r2 = nema.ring2 || [0, 0, 0, 0];
  const LBL = 34, CELL = 56, GAP = 3, ROW = 26, TOP = 4, PAD = 16;
  // PAD keeps the closing barrier and its label inside the viewBox; without it
  // both are clipped at the right edge.
  const W = LBL + 4 * CELL + 3 * GAP + PAD;
  const H = TOP + 2 * ROW + GAP + 16;
  const x = (i) => LBL + i * (CELL + GAP);

  const cell = (n, i, y, h, key) => {
    if (!n) {
      return (
        <rect key={key} x={x(i)} y={y} width={CELL} height={h} rx="3"
          fill="none" stroke="#3a3a3a" strokeDasharray="3 3" />
      );
    }
    const ph = byNumber[n];
    const fill = ph ? phaseColor(ph.state) : PHASE_COLOR.green;
    return (
      <g key={key}>
        <title>{describe(n)}</title>
        <rect x={x(i)} y={y} width={CELL} height={h} rx="3"
          fill={fill} fillOpacity="0.22" stroke={fill} strokeWidth="1.2" />
        <text x={x(i) + CELL / 2} y={y + h / 2 + 4} textAnchor="middle"
          fill={fill} fontSize="12" fontWeight="600">φ{n}</text>
      </g>
    );
  };

  const cells = [];
  for (let i = 0; i < 4; i++) {
    // A phase in the same slot of both rings is one movement group with no opposing
    // partner: the rings run it in lockstep, so draw one tall cell.
    if (r1[i] && r1[i] === r2[i]) {
      cells.push(cell(r1[i], i, TOP, 2 * ROW + GAP, `b${i}`));
    } else {
      cells.push(cell(r1[i], i, TOP, ROW, `a${i}`));
      cells.push(cell(r2[i], i, TOP + ROW + GAP, ROW, `c${i}`));
    }
  }

  // Barriers: both rings must arrive together here. The middle one closes on
  // barrier2Phases (the coordinated phases, usually 2 and 6), the right-hand
  // edge on barrierPhases (usually 4 and 8).
  const barrier = (bx, phases, anchor, key) => (
    <g key={key}>
      <line x1={bx} y1={TOP - 3} x2={bx} y2={TOP + 2 * ROW + GAP + 3}
        stroke="#e0a13a" strokeWidth="2" />
      <text x={bx} y={H - 3} textAnchor={anchor} fill="#8a8a8a" fontSize="9">
        {(phases || []).map((n) => `φ${n}`).join('+')}
      </text>
    </g>
  );

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label="Ring and barrier structure">
      <text x="0" y={TOP + ROW / 2 + 4} fill="#9a9a9a" fontSize="10">Ring 1</text>
      <text x="0" y={TOP + ROW + GAP + ROW / 2 + 4} fill="#9a9a9a" fontSize="10">Ring 2</text>
      {cells}
      {barrier(x(2) - GAP / 2, nema.barrier2Phases, 'middle', 'b2')}
      {/* The closing barrier is also the start of the next cycle, so it is drawn
          once, at the end. */}
      {barrier(x(3) + CELL + 2, nema.barrierPhases, 'end', 'b1')}
    </svg>
  );
}

// Read-only. On a NEMA program a phase's `duration` is the CYCLE length, not its own
// green (netconvert passes tls.cycle.time to every addStep), and there is no fixed
// split at all — green runs actuated between minDur and maxDur. Editing max green is P3a.
function SignalEditor({ selected, onBack, onStageProgram, editing, setEditing, onHelp }) {
  const prog = selected.program;

  if (!prog) {
    return (
      <div className="ne-editor">
        <button className="ne-back" onClick={onBack}><FaArrowLeft size={11} /> Back</button>
        <div className="ne-editor-title">Signal — {selected.label}</div>
        <div className="ne-slider-hint">No timing program found for this junction.</div>
      </div>
    );
  }

  const nema = prog.nema || null;

  // Protected vs permissive is a property of the MOVEMENT, not the street: the yielding
  // positions are the left and U-turn off an approach whose through is protected
  // ('GGgg'). Naming phases by street alone prints the same label twice.
  const movementsForPhase = (state) => {
    const turnsByStreet = new Map(); // street -> Set(turn word)
    const yielding = new Map();      // street -> the turns off it that must yield
    for (const c of selected.connections || []) {
      const ch = state[c.linkIndex];
      if (ch !== 'G' && ch !== 'g') continue;
      if (!c.fromName) continue;
      const word = DIR_WORD[c.dir] || 'movement';
      const set = turnsByStreet.get(c.fromName) || new Set();
      set.add(word);
      turnsByStreet.set(c.fromName, set);
      if (ch === 'g') {
        const seen = yielding.get(c.fromName) || [];
        if (!seen.includes(word)) yielding.set(c.fromName, [...seen, word]);
      }
    }
    // Canonical order, so two phases on the same street are compared like with
    // like rather than in whatever order the connection list happened to be in.
    const ORDER = ['through', 'left', 'right', 'U-turn', 'slight left', 'slight right'];
    const bySort = (a, b) => ORDER.indexOf(a) - ORDER.indexOf(b);
    // Streets and turns returned apart: one combined string ellipsises at exactly the
    // turn in a 300 px panel — the part that distinguishes this phase from the next.
    const allTurns = new Set();
    for (const set of turnsByStreet.values()) for (const w of set) allTurns.add(w);
    return {
      names: [...turnsByStreet.keys()],
      turns: [...allTurns].sort(bySort).join(' / '),
      yielding: [...yielding].map(([n, turns]) => `${n} ${turns.join('/')}`),
    };
  };

  // On a NEMA program every phase is a green stage (clearance is carried as yellow/red
  // attributes), so this only filters the two actuated junctions.
  const stages = prog.phases
    .map((ph, i) => ({ ph, i }))
    .filter(({ ph }) => phaseKind(ph.state) === 'green');

  // Ring/barrier come from tlLogic params (netconvert, plus demo/bake/nema_split.py for
  // the junctions it declines). 0 = unused slot. Where a barrier side has no ring-2
  // phases, ring 2 repeats ring 1's. Chart is keyed by NEMA number, the list by program
  // order — this is the join.
  const byNumber = {};
  for (const ph of prog.phases) {
    if (ph.nema_phase != null) byNumber[ph.nema_phase] = ph;
  }

  const describe = (n) => {
    const ph = byNumber[n];
    if (!ph) return `φ${n}`;
    const { names, turns } = movementsForPhase(ph.state);
    return `φ${n} — ${names.join(' + ') || 'unnamed'}`
      + (turns ? ` (${turns})` : '')
      + `\ngreen ${secs(ph.minDur)}–${secs(ph.maxDur)}`
      + `, yellow ${secs(ph.yellow)}, all-red ${secs(ph.red)}`;
  };

  if (editing === selected.junctionId && nema) {
    return (
      <div className="ne-editor">
        <button className="ne-back" onClick={() => setEditing(null)}>
          <FaArrowLeft size={11} /> Back to structure
        </button>
        <div className="ne-editor-title">Signal — {selected.label}</div>
        <NemaProgramEditor
          nema={nema}
          byNumber={byNumber}
          movementsForPhase={movementsForPhase}
          onHelp={onHelp}
          onCancel={() => setEditing(null)}
          onStage={(program) => {
            setEditing(null);
            onStageProgram && onStageProgram(selected.junctionId, selected.tlsId, program);
          }}
        />
      </div>
    );
  }

  return (
    <div className="ne-editor">
      <button className="ne-back" onClick={onBack}><FaArrowLeft size={11} /> Back</button>
      <div className="ne-editor-title">Signal — {selected.label}</div>

      <div className="ne-explain">
        {nema
          ? 'A NEMA controller: eight possible phases in two rings, split by barriers.'
          : "More than NEMA's two rings hold, so this shows the plain actuated "
            + 'program the network carries. A real controller here would be a '
            + '12- or 16-phase unit.'}
        {' '}
        <button className="ne-link" onClick={onHelp}>How this works</button>
      </div>

      {/* Every timing here was guessed by netconvert from OSM; all 111 cycle lengths came
          out at exactly 90 s. */}
      <div className="ne-slider-hint">
        <b>Timings are synthetic.</b> They were generated from the road network,
        not surveyed from the controller, so treat any before/after as a
        comparison between two assumed states.
      </div>

      {nema && (
        <>
          <div className="ne-slider-hint">
            Cycle {secs(nema.cycle_s)} · {stages.length} phases ·{' '}
            {nema.source === 'derived-split'
              ? 'split phasing, structure derived here'
              : 'structure from the network build'}
          </div>
          <div className="ne-phase-list">
            <div className="ne-phase-item">
              <RingBarrierChart nema={nema} byNumber={byNumber} describe={describe} />
              <div className="ne-phase-turns">
                A phase spanning both rings has no opposing partner: the rings run
                it in lockstep, so those approaches are served one at a time.
                Cell widths are not to scale — this controller has no fixed split.
              </div>
            </div>
          </div>
        </>
      )}

      <Section title="Phase timing" count={stages.length}>
      <div className="ne-phase-list">
        {stages.map(({ ph, i }) => {
          const { names, turns, yielding } = movementsForPhase(ph.state);
          return (
            <div key={i} className="ne-phase-item">
              <div className="ne-phase-item-head">
                <span className="ne-phase-dot" style={{ background: phaseColor(ph.state) }} />
                <span className="ne-phase-streets">
                  {names.length ? names.join(' + ') : `Phase ${i + 1}`}
                </span>
                <span className="ne-slider-val">
                  {ph.nema_phase != null ? `φ${ph.nema_phase}` : `#${i + 1}`}
                </span>
              </div>
              {turns && <div className="ne-phase-turns">{turns}</div>}
              <div className="ne-phase-meta">
                <span className="ne-phase-bounds"
                  title={nema
                    ? 'Green runs between these bounds, extended by demand. Both are '
                      + "netconvert's defaults for this network, not surveyed values."
                    : 'Controller bounds declared in the source network.'}>
                  {nema
                    ? `Green ${secs(ph.minDur)}–${secs(ph.maxDur)}`
                    : `Green ${secs(ph.duration)}${ph.minDur != null ? `, min ${secs(ph.minDur)}` : ''}`}
                  {ph.yellow != null && ` · yellow ${secs(ph.yellow)}`}
                  {ph.red != null && ` · all-red ${secs(ph.red)}`}
                </span>
                {yielding.length > 0 && (
                  <span className="ne-phase-tag"
                    title={`Permissive: ${yielding.join(', ')} — green, but must yield `
                      + 'to oncoming traffic (SUMO lowercase "g"). Every other movement '
                      + 'in this phase is protected.'}>
                    yields
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </Section>

      {nema ? (
        <button className="ne-action stage" onClick={() => setEditing(selected.junctionId)}>
          Edit this program
        </button>
      ) : (
        <div className="ne-slider-hint">
          Read-only: there is no ring-barrier program to edit here. A 12- or
          16-phase controller is outside what SUMO's NEMA module expresses.
        </div>
      )}
    </div>
  );
}


// Measured per-approach delay from the microsimulation of a staged retiming.
function SignalResult({ signal, wholeProgram }) {
  if (!signal) return null;
  return (
    <div className="ne-impact">
      <div className="ne-section-label">
        Signal effect <span className="ne-est-tag">{signal.window_min}-min microsim</span>
      </div>
      <div className="ne-engine-note">
        Measured by a <b>{signal.window_min}-minute microsimulation</b> — the only
        engine here that queues cars at a red — and only for this junction.
        <b>One simulation draw</b>: with ~200 vehicles in the window the same edit
        can read ±15 points on another seed, so treat the sign as indicative, not
        measured. The retimed program also runs as a fixed cycle where the
        network's own was actuated.
      </div>
      <table className="ne-approach-table">
        <thead><tr><th>Approach</th><th>Wait change</th></tr></thead>
        <tbody>
          {signal.approaches.map((a) => (
            <tr key={a.edge_id}>
              <td>{a.name || a.edge_id}</td>
              <td className={a.waiting_delta_pct > 0 ? 'worse' : 'better'}>
                {a.waiting_delta_pct > 0 ? '+' : ''}{a.waiting_delta_pct}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* "Zero-sum" is true of a retiming and false of an authored program: the
          latter also changes the control regime -- a fixed cycle where the
          network's own program was free actuated -- so the total can and does
          move. Saying zero-sum there would be a claim the numbers contradict. */}
      <div className="ne-impact-note">
        Total approach delay {signal.totals.delta_pct > 0 ? '+' : ''}{signal.totals.delta_pct}%
        {wholeProgram
          ? ' — and this is your whole program against the one the network came '
            + 'with, so part of the difference is coordination and fixed cycle '
            + 'rather than the splits you set.'
          : ' — green time is zero-sum, retiming moves delay between approaches.'}
      </div>
    </div>
  );
}

// Staged edits as rows. Split out so the section header counts the same items the list
// renders — closing a street stages every segment and the list collapses them.
function pendingItems(edits, nameOf) {
  const items = [];
  // Closing a road stages every segment of it; show one row per street.
  const closedByName = {};
  edits.removed.forEach((id) => {
    const n = (nameOf && nameOf(id)) || '(unnamed road)';
    closedByName[n] = (closedByName[n] || 0) + 1;
  });
  Object.entries(closedByName).forEach(([n, c]) => items.push({
    k: `closed-${n}`, label: 'Closed road',
    detail: c > 1 ? `${n} (${c} segments)` : n,
  }));
  edits.added.forEach((a) => items.push({ k: a.id, label: 'New road', detail: a.name || a.id }));
  Object.entries(edits.attrs).forEach(([id, a]) => items.push({
    k: `attr-${id}`, label: `Changed ${(nameOf && nameOf(id)) || 'road'}`,
    // Staged attrs are stored in km/h; the summary reads back in mph.
    detail: [
      a.lanes != null ? `${a.lanes} lanes` : '',
      a.speed_kmh != null ? `@ ${kmhToMph(a.speed_kmh)} mph` : '',
    ].filter(Boolean).join(' '),
  }));
  // Two shapes share this slot: an authored ring-barrier program, and the older
  // flat phase list. Only one signal edit is staged at a time either way.
  if (edits.signal?.program) items.push({ k: 'signal', label: 'Ring-barrier program',
    detail: `${edits.signal.program.phases.length} phases, `
      + `${edits.signal.program.cycle}s cycle, `
      + `${edits.signal.program.coordinate ? 'coordinated' : 'free'}` });
  else if (edits.signal) items.push({ k: 'signal', label: 'Retimed signal',
    detail: `${edits.signal.phases.length} phases, ${Math.round(edits.signal.phases.reduce((s, p) => s + p.duration, 0))}s cycle` });
  return items;
}

function PendingEdits({ items }) {
  if (!items.length) return null;
  return (
    <div className="ne-pending">
      <div className="ne-section-label">Pending edits ({items.length})</div>
      <div className="ne-pending-list">
        {items.map((i) => (
          <div key={i.k} className="ne-pending-item">
            <span className="ne-pending-label">{i.label}</span>
            <span className="ne-pending-detail">{i.detail}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The panel's "Network Edits" section: staged changes, the run, and the way back.
 *  Open by default, unlike its Baseline Adjustments sibling. */
function NetworkEditsSection({
  items, hasEdits, phase, est, cost, runError, runResult, edits, safetyJob,
  busy, signalsOn, signalsAutoHidden, onToggleSignals,
  onExecute, onCostHelp, onReport, onResetEdits,
}) {
  const [open, setOpen] = useState(true);
  const runningHere = !!phase;
  useEffect(() => { if (runningHere) setOpen(true); }, [runningHere]);

  const count = items.length
    ? `${items.length} staged` : 'Nothing staged';
  const summary = runResult && !busy ? `${count} · simulated` : count;

  return (
    <>
      <button className="ne-sect" onClick={() => setOpen(!open)}>
        <span className="ne-sect-caret">{open ? '▾' : '▸'}</span>
        <span>Network Edits</span>
        <span className={`ne-sect-count ${items.length ? 'custom' : ''}`}>{summary}</span>
      </button>

      {open && (
        <div className="ne-sect-body">
          {/* `signalsAutoHidden` overrides the checkbox while the trip-flow
              layer is up; the user's own setting is kept and comes back. */}
          {onToggleSignals && (
            <>
              <label className={`ne-flow-toggle ne-signals-toggle${signalsAutoHidden ? ' is-locked' : ''}`}>
                <input type="checkbox"
                  checked={!signalsAutoHidden && !!signalsOn}
                  disabled={!!signalsAutoHidden}
                  onChange={(e) => onToggleSignals(e.target.checked)} />
                <span>Show signals</span>
              </label>
              {signalsAutoHidden && (
                <div className="ne-demand-hint ne-signals-hint muted">
                  Hidden while trip flows are shown — the zone circles and the
                  signal dots read as the same thing.
                </div>
              )}
              {!signalsAutoHidden && !signalsOn && (
                <div className="ne-demand-hint ne-signals-hint">
                  Signalized junctions are hidden. Turn this back on to click one
                  and edit its timing.
                </div>
              )}
            </>
          )}

          <PendingEdits items={items} />

          {/* `cost` is dropped while either run is in flight. The headline is the whole run;
              `costSoFar` is the macro block, which is what the "… so far" caption claims. */}
          <CostRow
            est={est}
            phase={phase}
            chainNote="netconvert → duarouter → metrics."
            cost={busy ? null : runCost(runResult)}
            costSoFar={runResult?.cost}
            hasWork={hasEdits}
            idleText="Click a road on the map to stage an edit."
            onHelp={() => onCostHelp(null)} />

          {hasEdits && (
            <button className="ne-execute" onClick={onExecute} disabled={busy}>
              {phase === 'macro'
                ? <><FaSpinner className="ne-spin" size={13} /> Running SUMO simulation…</>
                : <><FaPlay size={12} /> Run simulation</>}
            </button>
          )}
          {runError && <div className="ne-run-error">{runError}</div>}
          {runResult?.signal && !busy && (
            <SignalResult signal={runResult.signal}
              wholeProgram={!!edits?.signal?.program} />
          )}
          {runResult && !busy && (
            <button className="ne-report-btn" onClick={onReport}>
              <FaFileAlt size={12} /> Open report
            </button>
          )}
          {runResult && !busy && (
            <div className="ne-run-ok">
              Simulated {runResult.vehicles?.toLocaleString() || ''} trips · map updated
            </div>
          )}

          {/* Only the ways the conflict analysis ENDED; progress is the bar above.
              `safety_blocked` rides on the previous RESULT, still on screen. */}
          {!busy && <SafetyStatus job={safetyJob} result={runResult} />}

          {(hasEdits || runResult) && !busy && (
            <button className="ne-reset" onClick={onResetEdits}>
              <FaUndo size={11} /> Reset to the original network
            </button>
          )}
        </div>
      )}
    </>
  );
}

export default function NetworkEditorPanel({
  panelRef, position, isDragging, onDragStart, onClose,
  manifest, hasMetrics, cbsa, region, loading, loadError, selected, onClearSelection,
  summary, baseSummary, edits, hasEdits, running, runError, runResult, nameOf,
  costModel,
  demand, demandReport, demandResult, demandRunning, demandError, demandScale,
  bakedTrips,
  demandResetKey, onApplyDemand, onDemandReport, onDemandJson,
  flows, flowsOn, onToggleFlows, flowMode, onFlowMode, flowFade, onFlowFade,
  signalsOn, signalsAutoHidden, onToggleSignals,
  safetyJob, safetyJobOwner,
  onCloseRoad, onReopen, onChangeAttrs, onStageSignal, onStageProgram,
  onExecute, onResetEdits, onResetBaseline, onReport,
}) {
  // Editing state lives here, not in SignalEditor, because two things outside
  // that component depend on it: the panel widens while a program is open, and
  // the help sheet is positioned against the panel's own edge.
  const [editingProgram, setEditingProgram] = useState(null);
  // One slot, three sheets ('signal' | 'cost' | 'capacity'): they occupy the
  // same space beside the panel, so opening one has to close the other.
  const [helpOpen, setHelpOpen] = useState(null);
  // Which demand the cost sheet describes. Apply prices a demand with no edits; the
  // edit side prices staged edits under the applied demand. Null = opened from the edit side.
  const [costHelpDemand, setCostHelpDemand] = useState(null);
  const openCostHelp = (pendingDemand) => {
    setCostHelpDemand(pendingDemand || null);
    setHelpOpen('cost');
  };
  const panelWidth = editingProgram ? 380 : 300;

  // A run of either kind is in flight: the two buttons drive one pipeline and one map,
  // so every "last run" line stands down for both.
  const busy = running || demandRunning;

  // A run is two pieces of work (macro chain, then a conflict microsim on a worker) and
  // the bar spans both, so the phase outlives `running`. `safetyJobOwner` says which
  // button started the singleton job.
  const jobLive = !!safetyJob && ['queued', 'running'].includes(safetyJob.status);
  const editPhase = running ? 'macro'
    : (jobLive && safetyJobOwner === 'edit' ? 'safety' : null);
  const demandPhase = demandRunning ? 'macro'
    : (jobLive && safetyJobOwner === 'demand' ? 'safety' : null);

  const pending = pendingItems(edits, nameOf);

  // The authored program is staged like any other edit, so Run picks it up.
  const stagedProgram = edits?.signal?.program
    ? { junctionId: edits.signal.junctionId, program: edits.signal.program }
    : null;

  const dCong = runResult && baseSummary
    ? summary.congested_edges - baseSummary.congested_edges : null;

  // Leaving discards every staged edit, the run, and any custom demand. In-panel prompt,
  // not window.confirm: a native dialog blocks the event loop and would stall deck.gl
  // mid-frame and an in-flight request.
  const [confirmExit, setConfirmExit] = useState(false);
  const dirty = hasEdits || !!demand;
  const requestExit = () => (dirty ? setConfirmExit(true) : onClose());

  return (
    <>
    {/* Fixed to the viewport, not the panel: the panel is draggable and can be dragged
        off-screen. */}
    <div className="ne-exit-float">
      <button className="ne-exit-btn" onClick={requestExit}
        title="Leave the Network Editor">
        <FaSignOutAlt size={14} /> Exit Network Editor
      </button>

      {confirmExit && dirty && (
        <div className="ne-exit-confirm">
          <div className="ne-exit-confirm-text">
            Leaving discards your staged edits{runResult ? ' and the simulated result' : ''}
            {demand ? ', your custom demand,' : ''} and
            restores the original network{demand ? ' and its baked demand' : ''}.
          </div>
          <div className="ne-exit-confirm-actions">
            <button className="ne-exit-keep" onClick={() => setConfirmExit(false)}>
              Keep editing
            </button>
            <button className="ne-exit-discard" onClick={() => { setConfirmExit(false); onClose(); }}>
              Discard &amp; exit
            </button>
          </div>
        </div>
      )}
    </div>

    <div
      ref={panelRef}
      className={`ne-panel ${isDragging ? 'dragging' : ''} ${editingProgram ? 'wide' : ''}`}
      style={{ left: position.x, top: position.y, cursor: isDragging ? 'grabbing' : 'grab' }}
      onMouseDown={onDragStart}
    >
      <div className="ne-panel-header">
        <span>Network Editor{region ? ` — ${region}` : ''}</span>
        <button className="ne-close-btn" onClick={requestExit} title="Close">
          <FaTimes size={14} />
        </button>
      </div>

      {loadError && <div className="ne-loading error">{loadError}</div>}
      {loading && !loadError
        && <div className="ne-loading">Loading {region || 'the'} network…</div>}

      {!loading && (
        <div className="ne-body">
          {selected?.kind === 'road' && (
            <RoadEditor selected={selected} onClose={onCloseRoad} onReopen={onReopen}
              onChangeAttrs={onChangeAttrs} onBack={onClearSelection} />
          )}

          {selected?.kind === 'junction' && (
            <SignalEditor selected={selected} onBack={onClearSelection}
              onStageProgram={onStageProgram}
              editing={editingProgram} setEditing={setEditingProgram}
              onHelp={() => setHelpOpen('signal')} />
          )}

          {stagedProgram && (
            <div className="ne-phase-list">
              <div className="ne-phase-item">
                <div className="ne-phase-item-head">
                  <span className="ne-phase-streets">
                    Staged program — junction {stagedProgram.junctionId}
                  </span>
                  <button className="ne-link" onClick={() => onStageProgram(null)}>Discard</button>
                </div>
                <div className="ne-phase-turns">
                  cycle {stagedProgram.program.cycle}s · offset{' '}
                  {stagedProgram.program.offset}s ({stagedProgram.program.controllerType}) ·{' '}
                  {stagedProgram.program.coordinate ? 'coordinated' : 'free'} · min recall{' '}
                  {stagedProgram.program.minRecall.map((n) => `φ${n}`).join(', ') || 'none'}
                </div>
                {stagedProgram.program.phases.map((ph) => (
                  <div key={ph.nema_phase} className="ne-phase-turns">
                    φ{ph.nema_phase}: green {ph.maxDur}s · yellow {ph.yellow}s ·
                    all-red {ph.red}s · min {ph.minDur}s · ext {ph.vehExt}s
                  </div>
                ))}
                <div className="ne-phase-turns">
                  Run writes this as a SUMO additional-file with
                  <code> programID="NEMA"</code> and microsimulates it against the
                  network's own program.
                </div>
              </div>
            </div>
          )}

          {!selected && (
            <>
              <div className="ne-intro">
                <b>Edit the network, then run the simulation.</b><br />
                Click a <b>road</b> to close or modify it, or a <b>signal dot</b> to inspect its
                phasing.
              </div>

              {/* Above the totals, not inside the Demand section: it answers
                  "what am I looking at" for the numbers and the map colours,
                  which is a question the user asks here, not down there. */}
              {demand && !demandRunning && (
                <div className="ne-demand-badge">
                  Baseline updated — colours show your demand
                </div>
              )}

              {summary && (
                <>
                  <div className="ne-section-label">Network-wide · static assignment</div>
                  <div className="ne-stats">
                    <Stat label="Congested roads" value={summary.congested_edges} delta={dCong} />
                    <Stat label="Total VMT" value={Math.round(summary.total_vmt_mi).toLocaleString()} unit=" mi" />
                    <Stat label="Mean V/C" value={summary.mean_vc?.toFixed(3)} />
                  </div>
                  {/* The map and these totals share one engine, and it is not the
                      one a retiming runs. Saying so here is the whole point: the
                      panel otherwise reads as if the colours answered the signal
                      edit sitting a few pixels below them. */}
                  <div className="ne-engine-note">
                    These totals and the map colours come from <b>duarouter</b>'s
                    static assignment (BPR). It has no notion of signal timing, so
                    retiming a signal leaves them unchanged.
                  </div>
                  {/* Capacity is not flat lanes × 1800; the pce comes from whatever vehicle types are
                      loaded, so this line must not name fixed values. */}
                  <div className="ne-engine-note">
                    Capacity is discounted by how much road each vehicle takes —
                    the <b>pce</b> value on every vehicle type: a bus or a heavy
                    truck counts as two cars, anything on four tyres as one.{' '}
                    <button className="ne-link" onClick={() => setHelpOpen('capacity')}>
                      How this works
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Hidden, not unmounted: an uploaded matrix lives in this component's state and
              selecting a road tears the main view down. `key` is the opposite — Reset rebuilds it. */}
          <div hidden={!!selected}>
            <DemandSection
              key={demandResetKey}
              cbsa={cbsa}
              demandScale={demandScale}
              bakedTrips={bakedTrips}
              applied={demand}
              appliedReport={demandReport}
              appliedResult={demandResult}
              applying={demandRunning}
              applyError={demandError}
              phase={demandPhase}
              safetyJob={safetyJobOwner === 'demand' ? safetyJob : null}
              flows={flows}
              flowsOn={flowsOn}
              onToggleFlows={onToggleFlows}
              flowMode={flowMode}
              onFlowMode={onFlowMode}
              flowFade={flowFade}
              onFlowFade={onFlowFade}
              onApply={onApplyDemand}
              onReport={onDemandReport}
              onDownloadJson={onDemandJson}
              onCapacityHelp={() => setHelpOpen('capacity')}
              onCostHelp={openCostHelp}
              onResetBaseline={onResetBaseline}
            />
          </div>

          {!selected && (
            <>
              <NetworkEditsSection
                items={pending}
                hasEdits={hasEdits}
                phase={editPhase}
                est={estimateCost(costModel, edits, demand)}
                edits={edits}
                runError={runError}
                runResult={runResult}
                safetyJob={safetyJobOwner === 'edit' ? safetyJob : null}
                busy={busy}
                signalsOn={signalsOn}
                signalsAutoHidden={signalsAutoHidden}
                onToggleSignals={onToggleSignals}
                onExecute={onExecute}
                onCostHelp={openCostHelp}
                onReport={onReport}
                onResetEdits={onResetEdits}
              />

              <div className="ne-legend">
                <div className="ne-legend-title">
                  {hasMetrics ? 'Level of Service (V/C)'
                    : 'No produced run for this metro — V/C after a simulation'}
                </div>
                <div className="ne-legend-row" hidden={!hasMetrics}>
                  {LOS_LEGEND.map((l) => (
                    <div
                      key={l.grade}
                      className="ne-legend-item"
                      /* This ramp only has room for the letter; the band and its
                         meaning come from the same table, on hover. */
                      title={`LOS ${l.grade} — V/C ${l.range} — ${l.desc}`}
                    >
                      <span className="ne-legend-swatch" style={{ background: rgb(l.color) }} />{l.grade}
                    </div>
                  ))}
                  <div className="ne-legend-item">
                    <span className="ne-legend-swatch" style={{ background: '#46464e' }} />closed
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>

    {/* Beside the panel, not over it; flips to the left when the panel is far right. */}
    {helpOpen && (() => {
      const style = position.x > 360
        ? { left: Math.max(8, position.x - 332), top: position.y }
        : { left: position.x + panelWidth + 12, top: position.y };
      const close = () => setHelpOpen(null);
      if (helpOpen === 'cost') {
        // From Apply the sheet describes a demand with no edits; from the edit side, staged
        // edits under the applied demand. Same lookup, two keys.
        const helpEdits = costHelpDemand ? {} : edits;
        const helpDemand = costHelpDemand || demand;
        const est = estimateCost(costModel, helpEdits, helpDemand);
        const total = Object.values(costModel?.estimates || {})
          .reduce((n, e) => n + (e.samples || 0), 0);
        return <CostHelp onClose={close} style={style} rate={costModel?.rate}
          samples={est?.samples || 0} totalSamples={total}
          measured={costModel?.measured?.[editKindKey(helpEdits, helpDemand)] || 0} />;
      }
      if (helpOpen === 'capacity') {
        return <CapacityHelp onClose={close} style={style} />;
      }
      return <SignalHelp onClose={close} style={style} />;
    })()}
    </>
  );
}
